// src/components/Layout/Sidebar.js
import React, { useState, useContext, useEffect } from 'react';
import { NavLink, Link } from 'react-router-dom';
import {useNavigate } from 'react-router-dom';
import styles from './Sidebar.module.css'; // Ensure this path is correct
import EcomBuddhaLogoIcon from '../../assets/icons/buddha.png';// Ensure this path is correct
import StarIcon from '../../assets/dashboard_assets/star.png'; // Star icon for badge
import {
    FaTools,
} from 'react-icons/fa';
import Modal from '../UI/Modal'; // Ensure this path is correct
import CreateListingModal from '../UI/CreateListingModal'; // Import the new modal
import AuthContext from '../../context/AuthContext'; // Ensure this path is correct
import { useFormStep } from '../../context/FormStepContext'; // Import FormStepContext

// --- Import Download Libraries ---
// Note: We're now using S3 URLs instead of local generation

// --- S3 Template Download URLs ---
const NEW_LISTING_TEMPLATE_S3_URL = 'https://ecombuddha-application.s3.ap-south-1.amazonaws.com/Bulk_New_Sample.xlsx';
const EXISTING_LISTING_TEMPLATE_S3_URL = 'https://ecombuddha-application.s3.ap-south-1.amazonaws.com/Bulk_Existing_Sample.xlsx';

// Note: Template headers and sample data are no longer needed since we're downloading from S3

// Note: Sample data objects are no longer needed since we're downloading from S3

const Sidebar = ({ showBothSidebars = false }) => {
    const authCtx = useContext(AuthContext);
    const { setShowBothSidebars } = useFormStep();
    const [isGenerateOpen, setIsGenerateOpen] = useState(false);
    const [isTemplatesOpen, setIsTemplatesOpen] = useState(false);
    const [isUpcomingOpen, setIsUpcomingOpen] = useState(false);
    const [isListingScoreOpen, setIsListingScoreOpen] = useState(false); // NEW: Listing Score dropdown
    const [isToolsOpen, setIsToolsOpen] = useState(false); // NEW: Tools dropdown
    const [showUpcomingModal, setShowUpcomingModal] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false); // Loading state for downloads
    const [isModalOpen, setIsModalOpen] = useState(false); //modal to choose new/existing
    const [targetPath, setTargetPath] = useState(null);
    const [isCreateListingModalOpen, setIsCreateListingModalOpen] = useState(false); // Create listing modal
    const [hasListingScoreAccess, setHasListingScoreAccess] = useState(false);
    const [accessCheckLoading, setAccessCheckLoading] = useState(true);

    const navigate = useNavigate();

    // Check user access on component mount - only when user/token actually changes
    useEffect(() => {
        // Only check access if we have both user and token, and haven't checked yet
        if (authCtx.user && authCtx.token && !hasListingScoreAccess && !accessCheckLoading) {
            checkUserAccess();
        }
    }, [authCtx.user?.sub, authCtx.token]); // Only depend on user ID and token, not the entire user object

    const checkUserAccess = async () => {
        if (!authCtx.user || !authCtx.token) {
            setHasListingScoreAccess(false);
            setAccessCheckLoading(false);
            return;
        }
        
        // Prevent multiple simultaneous calls
        if (accessCheckLoading) {
            return;
        }

        try {
            const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
            const response = await fetch(`${API_URL}/listing_score/user-access`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${authCtx.token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                setHasListingScoreAccess(data.has_access);
            } else {
                setHasListingScoreAccess(false);
            }
        } catch (error) {
            setHasListingScoreAccess(false);
        } finally {
            setAccessCheckLoading(false);
        }
    };

    // Modal Handler for new/existing forms
    const handleGeneratorClick = (path) => {
        setTargetPath(path);
        setIsModalOpen(true);
    };
    
    const handleModalClose = () => {
        setIsModalOpen(false);
        setTargetPath(null);
    };

    const handleModalConfirm = (isNewListing) => {
        setIsModalOpen(false);
        if (targetPath) {
            navigate(targetPath, { state: { isNewListing: isNewListing } });
        }
        setTargetPath(null);
    };

    // Create Listing Modal Handlers
    const handleCreateListingClick = () => {
        setIsCreateListingModalOpen(true);
    };

    const handleCreateListingModalClose = () => {
        setIsCreateListingModalOpen(false);
    };

    // Buy Credits — redirect to plans page
    const handleBuyCreditsClick = (e) => {
        e.preventDefault();
        navigate('/dashboard/plans');
    };

    const handleCreateListingOption = (type, isNewListing, projectName) => {
    setIsCreateListingModalOpen(false);
    if (type === 'single') {
        navigate('/dashboard/generate/single', { 
        state: { 
            isNewListing: isNewListing,
            projectName: projectName 
        } 
        });
    } else if (type === 'bulk') {
        navigate('/dashboard/generate/bulk', { 
        state: { 
            isNewListing: isNewListing,
            projectName: projectName 
        } 
        });
    }
    };

    // Modal Handlers
    const handleOpenUpcomingModal = (event) => {
        event.preventDefault(); // Prevent default if it's a link/button
        setShowUpcomingModal(true);
    };
    const handleCloseUpcomingModal = () => {
        setShowUpcomingModal(false);
    };

    // Handle listing score click
    const handleListingScoreClick = (event) => {
        event.preventDefault();
        if (hasListingScoreAccess) {
            navigate('/dashboard/listing-score');
        } else {
            handleOpenUpcomingModal(event);
        }
    };

    // NavLink class getters
    const getNavLinkClass = ({ isActive }) => {
        return isActive ? `${styles.navLink} ${styles.active}` : styles.navLink;
    };
    const getSubNavLinkClass = ({ isActive }) => {
        return isActive ? `${styles.subNavLink} ${styles.active}` : styles.subNavLink;
    };

    // Toggle handlers for dropdown menus
    const toggleGenerateMenu = () => setIsGenerateOpen(prevState => !prevState);
    const toggleTemplatesMenu = () => setIsTemplatesOpen(prevState => !prevState);
    const toggleUpcomingMenu = () => setIsUpcomingOpen(prevState => !prevState);
    const toggleListingScoreMenu = () => setIsListingScoreOpen(prevState => !prevState); // NEW
    const toggleToolsMenu = () => setIsToolsOpen(prevState => !prevState); // NEW: Tools dropdown

    // Sign Out Handler
    const handleSignOut = () => {
        authCtx.logout(); // Assuming logout method exists on context
    };

    // --- S3 Template Download Function ---
    const downloadTemplateFromS3 = async (templateUrl, filename) => {
        if (isDownloading) return; // Prevent concurrent downloads
        setIsDownloading(true);

        try {
            const response = await fetch(templateUrl);
            if (!response.ok) {
                throw new Error(`Failed to download template: ${response.status} ${response.statusText}`);
            }
            
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            alert(`Failed to download template: ${error.message}`);
        } finally {
            setIsDownloading(false);
        }
    };

    // --- Specific Template Download Handlers Using S3 URLs ---
    const handleDownloadNewTemplate = () => {
        downloadTemplateFromS3(NEW_LISTING_TEMPLATE_S3_URL, "new_listing_template_with_samples.xlsx");
    };

    const handleDownloadExistingTemplate = () => {
        downloadTemplateFromS3(EXISTING_LISTING_TEMPLATE_S3_URL, "existing_listing_refine_template_with_samples.xlsx");
    };


    // --- Render Component ---
    return (
        <> {/* Use Fragment to wrap Sidebar and Modal */}
            <aside className={`${styles.sidebar} ${styles.scrollbarHide} ${showBothSidebars ? styles.sidebarNoBorderRadius : ''}`}>
                                {/* Logo Link */}
                <Link to="/dashboard" className={styles.logoLink}>
                    <div className={styles.sidebarHeaderContent}>
                    <img 
                        src={EcomBuddhaLogoIcon} 
                        className={styles.logoIcon} 
                        alt="EcomBuddha Logo" 
                    />
                    </div>
                </Link>

                {/* Primary Navigation */}
                <nav className={styles.sidebarNav}>
                   <ul>
                        {/* 1. Create / Close Menu Arrow */}
                        <li className={styles.navItemVertical}>
                            {showBothSidebars ? (
                                <>
                                    <button onClick={() => setShowBothSidebars(false)} className={styles.navIconLink}>
                                    <svg width="13" height="23" viewBox="0 0 13 23" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M0.528962 21.6253C-0.175912 20.8791 -0.176547 19.7127 0.527514 18.9658L6.6335 12.488C7.35969 11.7176 7.35969 10.5148 6.6335 9.74437L0.527515 3.26664C-0.176546 2.51972 -0.175912 1.35331 0.528962 0.607149C1.29352 -0.202191 2.58129 -0.202192 3.34585 0.607148L11.976 9.74279C12.7042 10.5136 12.7042 11.7188 11.976 12.4896L3.34585 21.6253C2.58129 22.4346 1.29352 22.4346 0.528962 21.6253Z" fill="#A4A4A4" fillOpacity="0.6"/>
                                    </svg>
                                    </button>
                                    <span className={styles.navLabel}>Close</span>
                                </>
                            ) : (
                                <>
                                    <button onClick={handleCreateListingClick} >
                                        <svg width="40" height="40" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <rect width="32" height="32" rx="6.4" fill="url(#paint0_linear_1_14)"/>
                                            <path d="M16.0003 9.84026C16.5745 9.84047 17.0404 10.3061 17.0404 10.8803V14.9604H21.1195C21.6938 14.9604 22.1595 15.426 22.1595 16.0004C22.1593 16.5746 21.6937 17.0405 21.1195 17.0405H17.0404V21.1196C17.0404 21.6938 16.5745 22.1594 16.0003 22.1596C15.4259 22.1596 14.9603 21.6939 14.9603 21.1196V17.0405H10.8802C10.306 17.0405 9.84038 16.5746 9.84017 16.0004C9.84017 15.426 10.3058 14.9604 10.8802 14.9604H14.9603V10.8803C14.9603 10.3059 15.4259 9.84026 16.0003 9.84026Z" fill="white" stroke="#175CAD" strokeWidth="0.48"/>
                                            <defs>
                                                <linearGradient id="paint0_linear_1_14" x1="-1.19209e-07" y1="16" x2="29.3714" y2="32" gradientUnits="userSpaceOnUse">
                                                    <stop stopColor="#2766AA"/>
                                                    <stop offset="1" stopColor="#0752B0"/>
                                                </linearGradient>
                                            </defs>
                                        </svg>
                                    </button>
                                    <span className={styles.navLabel}>Create</span>
                                </>
                            )}
                        </li>

                        {/* 2. Home Link */}
                        <li className={styles.navItemVertical}>
                            <NavLink to="/dashboard" end className={({ isActive }) => 
                                `${styles.navIconLink} ${isActive ? styles.navIconLinkActive : ''}`
                            }>
                                <svg width="30" height="24" viewBox="0 0 30 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M14.9797 0.000164405C14.8223 0.00353938 14.6755 0.0556753 14.5344 0.152507L0.284475 11.4024C-0.0433783 11.6657 -0.0898967 12.1516 0.155569 12.4688C0.397506 12.7815 0.912115 12.8315 1.22197 12.586L15.0031 1.71109L28.7843 12.586C29.0941 12.8315 29.5946 12.7796 29.839 12.4688C30.0815 12.1604 30.0554 11.648 29.7218 11.4024L15.4719 0.152507C15.305 0.0425466 15.137 -0.00311682 14.9797 0.000164405ZM5.62819 10.0899L4.1282 11.2735V23.25C4.12822 23.4489 4.20724 23.6397 4.34789 23.7803C4.48854 23.921 4.67929 24 4.87819 24H12.0031C12.202 24 12.3928 23.921 12.5334 23.7803C12.6741 23.6397 12.7531 23.4489 12.7531 23.25V18.75C12.7531 17.4906 13.7437 16.5001 15.0031 16.5001C16.2626 16.5001 17.2531 17.4906 17.2531 18.75V23.25C17.2531 23.4489 17.3322 23.6397 17.4728 23.7803C17.6134 23.921 17.8042 24 18.0031 24H25.1281C25.327 24 25.5177 23.921 25.6584 23.7803C25.799 23.6397 25.878 23.4489 25.8781 23.25V11.2735L24.3781 10.0899V22.5H18.7531V18.75C18.7531 16.6855 17.0676 15.0001 15.0031 15.0001C12.9386 15.0001 11.2532 16.6855 11.2532 18.75V22.5H5.62819V10.0899Z" fill="#A4A4A4"/>
                                </svg>
                            </NavLink>
                            <NavLink to="/dashboard" end className={({ isActive }) => 
                                isActive ? `${styles.navLabel} ${styles.navLabelActive}` : styles.navLabel
                            } style={{ textDecoration: 'none' }}>
                                Home
                                </NavLink>
                        </li>

                        {/* 3. Projects */}
                        <li className={styles.navItemVertical}>
                            <NavLink
                                to="/dashboard/projects"
                                className={({ isActive }) =>
                                    `${styles.navIconLink} ${isActive ? styles.navIconLinkActive : ''}`
                                }
                                onClick={(e) => {
                                    e.preventDefault();
                                    navigate('/dashboard/projects', { state: { resetView: Date.now() } });
                                }}
                            >
                                <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M10.6843 8H16.5138C17.2016 8 17.8263 8.28123 18.2797 8.73371C18.7331 9.18619 19.0134 9.81178 19.0134 10.4996V16.3291C19.0134 17.0169 18.7331 17.6416 18.2797 18.095C17.8272 18.5475 17.2016 18.8287 16.5138 18.8287H10.6843C9.99647 18.8287 9.37181 18.5475 8.9184 18.095C8.46592 17.6416 8.18469 17.0169 8.18469 16.3291V10.4996C8.18469 9.81178 8.46592 9.18712 8.9184 8.73371C9.37088 8.28123 9.99647 8 10.6843 8ZM23.4863 21.1713H29.3158C30.0036 21.1713 30.6283 21.4525 31.0817 21.905C31.5342 22.3584 31.8154 22.9831 31.8154 23.6709V29.5004C31.8154 30.1882 31.5342 30.8129 31.0817 31.2663C30.6292 31.7188 30.0036 32 29.3158 32H23.4863C22.7985 32 22.1738 31.7188 21.7204 31.2663C21.267 30.8138 20.9867 30.1882 20.9867 29.5004V23.6709C20.9867 22.9831 21.267 22.3584 21.7204 21.905C22.1729 21.4525 22.7985 21.1713 23.4863 21.1713ZM29.3158 22.2963H23.4863C23.1084 22.2963 22.766 22.4512 22.5153 22.6999C22.2666 22.9486 22.1117 23.293 22.1117 23.6709V29.5004C22.1117 29.8773 22.2666 30.2207 22.5153 30.4704C22.764 30.7191 23.1084 30.8741 23.4863 30.8741H29.3158C29.6937 30.8741 30.0361 30.7191 30.2858 30.4704C30.5345 30.2217 30.6895 29.8773 30.6895 29.5004V23.6709C30.6895 23.293 30.5345 22.9506 30.2858 22.6999C30.0371 22.4512 29.6927 22.2963 29.3158 22.2963ZM10.6843 21.1713H16.5138C17.2016 21.1713 17.8263 21.4525 18.2797 21.905C18.7331 22.3584 19.0134 22.9831 19.0134 23.6709V29.5004C19.0134 30.1882 18.7331 30.8129 18.2797 31.2663C17.8272 31.7188 17.2016 32 16.5138 32H10.6843C9.99647 32 9.37181 31.7188 8.9184 31.2663C8.46592 30.8138 8.18469 30.1882 8.18469 29.5004V23.6709C8.18469 22.9831 8.46592 22.3584 8.9184 21.905C9.37088 21.4525 9.99647 21.1713 10.6843 21.1713ZM16.5138 22.2963H10.6843C10.3064 22.2963 9.96395 22.4512 9.71333 22.6999C9.46462 22.9486 9.30966 23.293 9.30966 23.6709V29.5004C9.30966 29.8773 9.46463 30.2207 9.71333 30.4704C9.963 30.7191 10.3064 30.8741 10.6843 30.8741H16.5138C16.8917 30.8741 17.2341 30.7191 17.4848 30.4704C17.7335 30.2217 17.8884 29.8773 17.8884 29.5004V23.6709C17.8884 23.293 17.7335 22.9506 17.4848 22.6999C17.236 22.4512 16.8917 22.2963 16.5138 22.2963ZM23.0665 10.1075H27.6295C28.2016 10.1075 28.722 10.3409 29.0979 10.7178C29.4739 11.0937 29.7073 11.6141 29.7073 12.1861V16.7491C29.7073 17.3212 29.4739 17.8416 29.0979 18.2175C28.721 18.5944 28.2016 18.8278 27.6295 18.8278H23.0665C22.4945 18.8278 21.9741 18.5944 21.5982 18.2175C21.2222 17.8406 20.9888 17.3212 20.9888 16.7491V12.1861C20.9888 11.6141 21.2222 11.0937 21.5982 10.7178C21.9751 10.3409 22.4945 10.1075 23.0665 10.1075ZM27.6295 11.2324H23.0665C22.8054 11.2324 22.5662 11.3396 22.3931 11.5127C22.2209 11.6858 22.1128 11.924 22.1128 12.1861V16.7491C22.1128 17.0103 22.22 17.2494 22.3931 17.4226C22.5653 17.5957 22.8044 17.7029 23.0665 17.7029H27.6295C27.8907 17.7029 28.1298 17.5957 28.303 17.4226C28.4752 17.2504 28.5832 17.0112 28.5832 16.7491V12.1861C28.5832 11.925 28.4761 11.6858 28.303 11.5127C28.1298 11.3405 27.8916 11.2324 27.6295 11.2324ZM16.5138 9.12504H10.6843C10.3064 9.12504 9.96395 9.28001 9.71333 9.52871C9.46462 9.77742 9.30966 10.1218 9.30966 10.4987V16.3282C9.30966 16.7061 9.46463 17.0486 9.71333 17.2992C9.963 17.5479 10.3064 17.7029 10.6843 17.7029H16.5138C16.8917 17.7029 17.2341 17.5479 17.4848 17.2992C17.7335 17.0505 17.8884 16.7061 17.8884 16.3282V10.4987C17.8884 10.1218 17.7335 9.77838 17.4848 9.52871C17.236 9.28 16.8917 9.12504 16.5138 9.12504Z" fill="#A4A4A4" stroke="#A4A4A4" strokeWidth="0.2"/>
                                </svg>
                            </NavLink>
                            <NavLink
                                to="/dashboard/projects"
                                className={({ isActive }) =>
                                    isActive ? `${styles.navLabel} ${styles.navLabelActive}` : styles.navLabel
                                }
                                style={{ textDecoration: 'none' }}
                                onClick={(e) => {
                                    e.preventDefault();
                                    navigate('/dashboard/projects', { state: { resetView: Date.now() } });
                                }}
                            >
                                Projects
                            </NavLink>
                        </li>

                        {/* 4. Submitted */}
                        <li className={styles.navItemVertical}>
                            <NavLink to="/dashboard/amazon-submissions" className={({ isActive }) => 
                                `${styles.navIconLink} ${isActive ? styles.navIconLinkActive : ''}`
                            }>
                                <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M33.4237 21.7242L29.228 15.3781C29.1324 15.2171 28.9735 15.1529 28.7835 15.1529L25.4769 15.1516L27.034 12.7028L19.9142 8L15.3682 15.1514H11.2358C11.0769 15.1514 10.8856 15.2483 10.7913 15.3767L6.59561 21.724C6.53228 21.8209 6.5 21.9178 6.5 22.0135V31.484C6.5 31.7735 6.72227 32 7.00911 32H32.9794C33.265 32 33.4886 31.7747 33.4886 31.484L33.4873 22.0135C33.5196 21.9166 33.4873 21.7882 33.424 21.724L33.4237 21.7242ZM25.6357 12.9932L24.2686 15.1514L23.6005 16.2148L22.2967 18.2446H17.9755L15.5914 16.6665L15.9092 16.1833L16.5773 15.12L20.2329 9.41821L25.6357 12.9932ZM11.5225 16.2148H14.7013L14.1922 16.9874L16.0995 18.2433H13.8743C13.5887 18.2433 13.3652 18.4685 13.3652 18.7592C13.3652 19.0487 13.5875 19.2752 13.8743 19.2752H26.1126C26.3982 19.2752 26.6217 19.0499 26.6217 18.7592C26.6217 18.4698 26.3995 18.2433 26.1126 18.2433H24.301L23.5063 18.2445L24.8101 16.2147H28.5289L32.2802 21.8852H7.73908L11.5225 16.2148ZM32.4701 30.9696H7.54905V22.9158H32.4701V30.9696Z" fill="#A4A4A4"/>
                                </svg>
                            </NavLink>
                            <NavLink to="/dashboard/amazon-submissions" className={({ isActive }) => 
                                isActive ? `${styles.navLabel} ${styles.navLabelActive}` : styles.navLabel
                            } style={{ textDecoration: 'none' }}>
                                Submitted
                                </NavLink>
                        </li>

                        {/* 5. Integrations */}
                        <li className={styles.navItemVertical}>
                            <NavLink to="/dashboard/market-places" className={({ isActive }) => 
                                `${styles.navIconLink} ${isActive ? styles.navIconLinkActive : ''}`
                            }>
                                <svg width="28" height="24" viewBox="0 0 28 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M16.0399 18.0944H14.4369V13.546C14.4369 13.2842 14.2245 13.0718 13.9627 13.0718C13.7009 13.0718 13.4884 13.2842 13.4884 13.546V18.0944H11.8878C10.8256 18.0944 9.9621 18.9579 9.9621 20.0201V22.0732C9.9621 23.1354 10.8256 23.9989 11.8878 23.9989H16.0387C17.1009 23.9989 17.9644 23.1354 17.9644 22.0732V20.0201C17.9644 18.9579 17.1009 18.0944 16.0387 18.0944H16.0399ZM17.0159 22.0732C17.0159 22.6117 16.5773 23.0503 16.0388 23.0503H11.8878C11.3493 23.0503 10.9106 22.6117 10.9106 22.0732V20.02C10.9106 19.4815 11.3493 19.0429 11.8878 19.0429H16.0388C16.5773 19.0429 17.0159 19.4815 17.0159 20.02V22.0732Z" fill="#A4A4A4"/>
                                    <path d="M6.07655 18.0944H4.47128V12.1394C4.47128 10.8522 5.52194 9.80493 6.81259 9.80493H7.5004C7.7622 9.80493 7.97464 9.5925 7.97464 9.33069C7.97464 9.06888 7.76221 8.85645 7.5004 8.85645H6.81259C4.99831 8.85645 3.52291 10.3285 3.52291 12.1382V18.0932H1.92566C0.863502 18.0932 0 18.9567 0 20.0189V22.072C0 23.1341 0.863502 23.9976 1.92566 23.9976H6.07547C7.13762 23.9976 8.00113 23.1341 8.00113 22.072V20.0189C8.00113 18.9567 7.13762 18.0932 6.07547 18.0932L6.07655 18.0944ZM7.05373 22.0731C7.05373 22.6116 6.61508 23.0503 6.07655 23.0503H1.92675C1.38822 23.0503 0.949573 22.6116 0.949573 22.0731V20.02C0.949573 19.4814 1.38822 19.0428 1.92675 19.0428H6.07655C6.61508 19.0428 7.05373 19.4814 7.05373 20.02V22.0731Z" fill="#A4A4A4"/>
                                    <path d="M26.0012 18.0944H24.4005V12.1394C24.4005 10.3297 22.9284 8.85767 21.1188 8.85767H20.4229C20.1611 8.85767 19.9487 9.0701 19.9487 9.33191C19.9487 9.59372 20.1611 9.80615 20.4229 9.80615H21.1188C22.406 9.80615 23.4521 10.8534 23.4521 12.1406V18.0956H21.8502C20.7881 18.0956 19.9246 18.9591 19.9246 20.0213V22.0744C19.9246 23.1365 20.7881 24 21.8502 24H26C27.0622 24 27.9257 23.1365 27.9257 22.0744V20.0213C27.9257 18.9591 27.0622 18.0956 26 18.0956L26.0012 18.0944ZM26.9784 22.0731C26.9784 22.6117 26.5397 23.0503 26.0012 23.0503H21.8514C21.3129 23.0503 20.8742 22.6117 20.8742 22.0731V20.02C20.8742 19.4815 21.3129 19.0428 21.8514 19.0428H26.0012C26.5397 19.0428 26.9784 19.4815 26.9784 20.02V22.0731Z" fill="#A4A4A4"/>
                                    <path d="M13.9639 0C11.1369 0 9.0069 1.05757 9.0069 2.46075V9.58335C9.0069 10.9865 11.1381 12.0441 13.9639 12.0441C16.791 12.0441 18.9209 10.9865 18.9209 9.58335V2.46075C18.9209 1.05757 16.7897 0 13.9639 0ZM17.9726 6.02144C17.9726 6.65183 16.4477 7.5337 13.9639 7.5337C11.4813 7.5337 9.9552 6.65298 9.9552 6.02144V3.93963C10.8417 4.54247 12.2759 4.92139 13.9639 4.92139C15.652 4.92139 17.086 4.54244 17.9726 3.93963V6.02144ZM13.9639 0.948308C16.4465 0.948308 17.9726 1.82904 17.9726 2.46058C17.9726 3.09097 16.4477 3.97284 13.9639 3.97284C11.4813 3.97284 9.9552 3.09211 9.9552 2.46058C9.9552 1.83018 11.4801 0.948308 13.9639 0.948308ZM13.9639 11.0957C11.4802 11.0957 9.9552 10.215 9.9552 9.58347V7.50166C10.8417 8.10451 12.2759 8.48342 13.9639 8.48342C15.652 8.48342 17.086 8.10448 17.9726 7.50166V9.58347C17.9726 10.2139 16.4477 11.0957 13.9639 11.0957Z" fill="#A4A4A4"/>
                                    <path d="M16.4179 6.50042C16.7738 6.50042 17.0609 6.2122 17.0609 5.85739C17.0609 5.50257 16.7727 5.21436 16.4179 5.21436C16.0619 5.21436 15.7748 5.50257 15.7748 5.85739C15.7748 6.21334 16.0631 6.50042 16.4179 6.50042Z" fill="#A4A4A4"/>
                                    <path d="M16.4179 8.77515C16.0619 8.77515 15.7748 9.06336 15.7748 9.41818C15.7748 9.77413 16.0631 10.0612 16.4179 10.0612C16.7738 10.0612 17.0609 9.773 17.0609 9.41818C17.0609 9.06336 16.7727 8.77515 16.4179 8.77515Z" fill="#A4A4A4"/>
                                </svg>
                            </NavLink>
                            <NavLink to="/dashboard/market-places" className={({ isActive }) => 
                                isActive ? `${styles.navLabel} ${styles.navLabelActive}` : styles.navLabel
                            } style={{ textDecoration: 'none' }}>
                                Integrations
                                </NavLink>
                        </li>

                        {/* Separator */}
                        <li className={styles.separator}></li>

                        {/* Credits Badge */}
                        <li className={styles.badgeContainer}>
                            <div className={styles.badge} onClick={handleBuyCreditsClick} style={{ cursor: 'pointer' }}>
                                <div className={styles.badgeIcon}>
                                    <img src={StarIcon} alt="Credits" className={styles.starIcon} />
                                </div>
                                <span className={styles.badgeNumber}>
                                    {authCtx.user?.credits ?? 0}
                                </span>
                            </div>
                        </li>


                        {/* Image ZIP Upload (allowed emails only) */}
                        {authCtx.user?.email &&
                         ['sushantkrjha1@gmail.com', 'harshita@ecombuddha.in', 'bhavana@ecombuddha.in']
                           .includes(authCtx.user.email.toLowerCase()) && (
                            <li className={styles.navItemVertical}>
                                <NavLink
                                    to="/dashboard/tools/image-zip-upload"
                                    className={({ isActive }) =>
                                        `${styles.navIconLink} ${isActive ? styles.navIconLinkActive : ''}`
                                    }
                                >
                                    <FaTools color="#A4A4A4" />
                                </NavLink>
                                <NavLink
                                    to="/dashboard/tools/image-zip-upload"
                                    className={({ isActive }) =>
                                        isActive ? `${styles.navLabel} ${styles.navLabelActive}` : styles.navLabel
                                    }
                                    style={{ textDecoration: 'none' }}
                                >
                                    Image ZIP Upload
                                </NavLink>
                            </li>
                        )}

                        {/* 7. Referral */}
                        <li className={styles.navItemVertical}>
                            <NavLink to="/dashboard/referral" className={({ isActive }) =>
                                `${styles.navIconLink} ${isActive ? styles.navIconLinkActive : ''}`
                            }>
                                <svg width="30" height="20" viewBox="0 0 30 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M28.5636 0.0999756H0.476875C0.268465 0.0999756 0.0999985 0.268442 0.0999985 0.476852V18.7231C0.0999985 18.9315 0.268465 19.1 0.476875 19.1H28.5636C28.772 19.1 28.9405 18.9315 28.9405 18.7231V0.476852C28.9405 0.268442 28.772 0.0999756 28.5636 0.0999756ZM20.2846 18.3462V12.5196C20.2846 12.3112 20.1161 12.1428 19.9077 12.1428C19.6993 12.1428 19.5308 12.3112 19.5308 12.5196V18.3462H0.85376V9.97871H13.4295C13.6379 9.97871 13.8064 9.81025 13.8064 9.60184C13.8064 9.39343 13.6379 9.22496 13.4295 9.22496L0.85376 9.22322V0.85393H19.5308V7.43457C19.5308 7.64298 19.6993 7.81144 19.9077 7.81144C20.1161 7.81144 20.2846 7.64298 20.2846 7.43457V0.85393H28.1866V9.22144H26.3248C26.1164 9.22144 25.9479 9.38991 25.9479 9.59832C25.9479 9.80673 26.1164 9.9752 26.3248 9.9752H28.1866V18.3427H20.2846V18.3462ZM24.7653 7.69654L24.7497 7.68265C23.7702 6.80037 22.1724 6.80384 21.1894 7.68959L19.9094 8.84282L18.6294 7.68959C17.6447 6.80385 16.0486 6.80038 15.0691 7.68091L15.0517 7.69654C14.5672 8.13247 14.3014 8.71776 14.3014 9.34644C14.3014 9.97862 14.5706 10.5691 15.0604 11.0102C15.5536 11.4549 16.2014 11.6772 16.8492 11.6772C17.4918 11.6772 18.1344 11.4583 18.6225 11.0172L19.0775 10.6073C18.5165 11.8317 17.6134 12.9415 16.448 13.8168C16.2813 13.9419 16.2483 14.1781 16.3733 14.3448C16.448 14.4438 16.5609 14.4959 16.6755 14.4959C16.7537 14.4959 16.8336 14.4716 16.9013 14.4212C18.2751 13.3913 19.3137 12.0558 19.9093 10.5813C20.505 12.054 21.5454 13.3896 22.9174 14.4212C22.9851 14.4716 23.065 14.4959 23.1432 14.4959C23.2578 14.4959 23.3707 14.4438 23.4454 14.3448C23.5704 14.1781 23.5374 13.9419 23.3707 13.8168C22.2053 12.9415 21.3022 11.8318 20.7412 10.6073L21.1962 11.0172C21.6843 11.4566 22.3269 11.6772 22.9695 11.6772C23.6155 11.6772 24.2634 11.4549 24.7583 11.0102C25.2481 10.5691 25.5172 9.97862 25.5172 9.34644C25.5172 8.71945 25.2498 8.13243 24.767 7.69654H24.7653ZM18.1153 10.4562C17.4258 11.0762 16.2588 11.0728 15.5641 10.4493C15.2358 10.154 15.0552 9.76151 15.0552 9.34642C15.0552 8.93481 15.2324 8.54924 15.5571 8.25749L15.5745 8.24186C16.264 7.62185 17.4311 7.62532 18.1258 8.24881L19.3467 9.34819L18.1171 10.4545L18.1153 10.4562ZM24.2532 10.4493C23.5602 11.0745 22.3914 11.078 21.7019 10.4562L20.4723 9.3499L21.6932 8.25052C22.3862 7.62531 23.555 7.6218 24.2445 8.24357L24.2601 8.25747C24.5831 8.54924 24.762 8.93478 24.762 9.3464C24.762 9.76148 24.5814 10.1523 24.2532 10.4493ZM13.8551 1.9008L2.33178 1.89907C2.12337 1.89907 1.9549 2.06753 1.9549 2.27594V6.14541C1.9549 6.35382 2.12337 6.52228 2.33178 6.52228H13.8533C14.0617 6.52228 14.2302 6.35382 14.2302 6.14541V2.27594C14.2302 2.06753 14.0617 1.89907 13.8533 1.89907L13.8551 1.9008ZM13.4782 5.77027L2.70851 5.76853V2.65454H13.4765V5.76853L13.4782 5.77027ZM4.81373 3.55937C4.81373 3.35096 4.98219 3.18249 5.1906 3.18249H10.9967C11.2051 3.18249 11.3736 3.35096 11.3736 3.55937C11.3736 3.76778 11.2051 3.93624 10.9967 3.93624H5.1906C4.98219 3.93624 4.81373 3.76778 4.81373 3.55937ZM3.57021 4.8654C3.57021 4.65699 3.73867 4.48853 3.94708 4.48853H12.2399C12.4483 4.48853 12.6168 4.65699 12.6168 4.8654C12.6168 5.07381 12.4483 5.24228 12.2399 5.24228H3.9453C3.73689 5.24228 3.56843 5.07381 3.56843 4.8654H3.57021Z" fill="#A4A4A4" stroke="#A4A4A4" strokeWidth="0.2"/>
                                </svg>
                            </NavLink>
                            <NavLink to="/dashboard/referral" className={({ isActive }) =>
                                isActive ? `${styles.navLabel} ${styles.navLabelActive}` : styles.navLabel
                            } style={{ textDecoration: 'none' }}>
                                Referral
                            </NavLink>
                        </li>

                        {/* 8. Support */}
                        <li className={styles.navItemVertical}>
                            <NavLink to="/dashboard/help" className={({ isActive }) =>
                                `${styles.navIconLink} ${isActive ? styles.navIconLinkActive : ''}`
                            }>
                                <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M16.0022 6.06C12.0785 6.06 8.89954 9.20771 8.89954 13.0948V13.1894H8.72517C7.50459 13.1894 6.51182 14.1944 6.51182 15.4076V18.1265C6.51182 19.3407 7.50459 20.35 8.72517 20.35H11.113V20.3509C11.4381 20.35 11.7009 20.0888 11.7018 19.7668V13.7733C11.7009 13.4514 11.4381 13.1901 11.113 13.1901H10.0808V13.0956C10.0808 9.83498 12.712 7.2307 16.0022 7.2307C19.2925 7.2307 21.9237 9.83521 21.9237 13.0956V13.1901H20.8915C20.7346 13.1893 20.5839 13.2509 20.4727 13.3602C20.3614 13.4696 20.2992 13.618 20.2983 13.7734V19.7668C20.2992 19.9222 20.3614 20.0715 20.4727 20.1808C20.5839 20.2902 20.7347 20.3509 20.8915 20.3509H21.9237V20.5019C21.9237 21.8731 20.8118 22.9709 19.4273 22.9709H18.3329C18.0665 21.9425 17.1184 21.1744 16.002 21.1744C14.6807 21.1744 13.5967 22.2488 13.5967 23.5577C13.5967 24.8665 14.6806 25.94 16.002 25.94C17.1201 25.94 18.0682 25.1702 18.3329 24.1401H19.4273C21.4496 24.1401 23.1005 22.505 23.1005 20.502V20.351H23.2792C24.4998 20.351 25.4882 19.3417 25.4882 18.1275V15.4086C25.4882 14.1945 24.4998 13.1904 23.2792 13.1904H23.1005V13.0958C23.1005 9.2087 19.926 6.06099 16.002 6.06099L16.0022 6.06ZM8.72525 14.3601H10.5206V19.18H8.72525C8.15132 19.18 7.69217 18.7261 7.69217 18.1273V15.4084C7.69217 14.8096 8.15044 14.3601 8.72525 14.3601ZM21.4795 14.3601H23.2793C23.8532 14.3601 24.308 14.8096 24.308 15.4084V18.1274C24.308 18.7262 23.8532 19.1801 23.2793 19.1801H21.4795V14.3601ZM16.0022 22.3444C16.6848 22.3444 17.2263 22.8799 17.2263 23.5568C17.2263 24.2337 16.6848 24.7692 16.0022 24.7692C15.3197 24.7692 14.7781 24.2337 14.7781 23.5568C14.7781 22.8799 15.3197 22.3444 16.0022 22.3444Z" fill="#A4A4A4"/>
</svg>

                                </NavLink>
                            <NavLink to="/dashboard/help" className={({ isActive }) => 
                                isActive ? `${styles.navLabel} ${styles.navLabelActive}` : styles.navLabel
                            } style={{ textDecoration: 'none' }}>
                                Support
                                        </NavLink>
                        </li>
                        
                    </ul>
                </nav>
            </aside>

            {/* Modal Rendering */}
            <Modal
                show={showUpcomingModal}
                onClose={handleCloseUpcomingModal}
                title="Feature Coming Soon!"
            >
                <p>This feature is currently under development.</p>
                <p>We're working hard to bring it to you soon. Stay tuned!</p>
            </Modal>

            {/* Create Listing Modal */}
            <CreateListingModal
                show={isCreateListingModalOpen}
                onClose={handleCreateListingModalClose}
                onOptionSelect={handleCreateListingOption}
            />

        </>
    );
};

export default Sidebar;